import { Component, EventEmitter, inject, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import {
  ApplicantProduct,
  Product,
  MembershipService,
  APMembershipOptions
} from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { OaoShareCertificatesAccountComponent } from '../oao-share-certificates-account/oao-share-certificates-account.component';
import { firstValueFrom } from 'rxjs';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance } from '@workflow/class/field.service';

const SHARE_CERT_TYPE = ['retiresharecertificates', 'sharecertificates'];
/** OAO checking and saving accounts component */
@Component({
  selector: 'ecp-oao-included-account',
  templateUrl: './oao-included-account.component.html',
  standalone: false
})
export class OaoIncludedAccountComponent implements OnInit {
  private readonly statefulService = inject(StatefulService);

  /** oao account objcet */
  @Input() includedProductsAndServices: ApplicantProduct;

  /** oao account objcet */
  @Input() optionalProductsAndServices: ApplicantProduct;

  /** all account products */
  @Input() public allProducts: Product[];

  /** co Applicant Products */
  @Input() public coApplicantProducts: ApplicantProduct[];

  /** all Membership Services */
  @Input() public allServices: MembershipService[];
  /** field */
  @Input() public field: FieldInstance;
  /** share certificate options */
  @Input() public shareOptions: APMembershipOptions;

  @Output() public enableContinueButton = new EventEmitter();

  /** Share Certificate Child Component  */
  @ViewChildren(OaoShareCertificatesAccountComponent) private sharedCertificateComponent: QueryList<OaoShareCertificatesAccountComponent>;
  /** Included Products */
  public includedProducts: Product[] = [];

  /** Included MembershipServices */
  public includedMembershipServices: MembershipService[] = [];

  /** Optional Products */
  public optionalProducts: Product[] = [];

  /** Optional Products */
  public shareCertProducts: { type: string; products: Product[]; coAppProducts: ApplicantProduct[]; sortOrder: number }[] = [];

  /** Optional MembershipServices */
  public optionalMembershipServices: MembershipService[] = [];

  /** Co Applicants Share : Local Variable */
  public coAppProducts: ApplicantProduct[];

  /** Co Applicants optional Share : Local Variable */
  public coAppOptionalProducts: ApplicantProduct[];

  /** showing checking account flag */
  public showAccounts = false;
  /** Storage ID */
  public arrayStorageId = 'api:oaoMemberInformation/accountsAndServices/primaryApplicant/membershipServices';
  public coAppStorageId = 'api:oaoMemberInformation/accountsAndServices/coApplicant/membershipServices/$/isSelected';
  public additionalCoAppStorageId = 'api:oaoMemberInformation/accountsAndServices/additionalCoApplicant/membershipServices/$/isSelected';
  public responsibleParties4StorageId = 'api:oaoMemberInformation/accountsAndServices/responsibleParties4/membershipServices/$/isSelected';
  /** Optional Items combined and sorted by sortOrder */
  public combinedOptionalItems: Array<{ type: 'optional' | 'shareCertificate'; data: any; sortOrder: number }> = [];
  constructor(
    public readonly applicationApiService: ApplicationApiService,
    public readonly l360Service: L360Service,
    private readonly bundleHelperService: BundleHelperService
  ) {
    this.coAppProducts = [];
    this.coAppOptionalProducts = [];
  }

  public ngOnInit(): void {
    this.includedProducts = this.includedProductsAndServices.shares;
    this.includedMembershipServices = this.includedProductsAndServices.membershipServices;
    this.optionalProducts = this.optionalProductsAndServices.shares
      .filter((t) => !SHARE_CERT_TYPE.includes(t.type))
      .sort((a, b) => a.sortOrder - b.sortOrder);
    this.optionalMembershipServices = this.optionalProductsAndServices?.membershipServices;
    this.coAppProducts = this.coApplicantProducts.map((app) => {
      return {
        ...app,
        shares: app.shares.filter((s) => s.isIncluded)?.sort((a, b) => a.sortOrder - b.sortOrder),
        membershipServices: app.membershipServices.filter((s) => s.isIncluded)
      };
    });
    this.coAppOptionalProducts = this.coApplicantProducts.map((app) => {
      return {
        ...app,
        shares: app.shares
          .filter((s) => s.requiredOptInType.toLowerCase() === 'opt-out' && !SHARE_CERT_TYPE.includes(s.type))
          ?.sort((a, b) => a.sortOrder - b.sortOrder),
        membershipServices: app.membershipServices.filter((s) => s.requiredOptInType.toLowerCase() === 'opt-out')
      };
    });
    SHARE_CERT_TYPE.forEach((type) => {
      const products = this.optionalProductsAndServices.shares.filter((t) => t.type === type);
      const coAppProducts = this.coApplicantProducts.map((app) => {
        return {
          ...app,
          shares: app.shares.filter((s) => s.requiredOptInType.toLowerCase() === 'opt-out' && s.type === type)
        };
      });
      if (products.length) this.shareCertProducts.push({ type, products, coAppProducts, sortOrder: products[0]?.sortOrder || 0 });
    });
    // Add optional products
    this.optionalProducts.forEach((product) => {
      this.combinedOptionalItems.push({
        type: 'optional',
        data: product,
        sortOrder: product.sortOrder || 0
      });
    });

    // Add share certificate products
    this.shareCertProducts.forEach((product) => {
      this.combinedOptionalItems.push({
        type: 'shareCertificate',
        data: product,
        sortOrder: product.sortOrder || 0
      });
    });

    // Sort by sortOrder
    this.combinedOptionalItems.sort((a, b) => a.sortOrder - b.sortOrder);
    // Sort Membership Services
    this.optionalMembershipServices?.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  public getInvalidFields(): string[] {
    const invalidList = [];
    if (this.sharedCertificateComponent?.length) {
      this.sharedCertificateComponent.forEach((s) => {
        const shareCertificatesIds = s.getInvalidFieldIds();
        if (shareCertificatesIds && shareCertificatesIds.length > 0) invalidList.push(...shareCertificatesIds);
      });
    }
    return invalidList;
  }

  /** When primary account's selection is unchecked, clear all co applicant's shares */
  public primaryShareSelection(account: Product): void {
    // Clear primary Applicants services. When remove primar applicant's share, then clear all the services under the share
    if (!account.isSelected && account.optionalServices) {
      account.optionalServices.forEach((element) => {
        element.isSelected = false;
      });
    }

    // Clear Co-Applicants Shares. When remove primar applicant's share, then clear all the co applicant's share & services
    if (!account.isSelected) {
      this.coAppOptionalProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === account.id)
          .forEach((share) => {
            share.isSelected = false;
            share.isDisabled = false;
            share.optionalServices.forEach((service) => {
              service.isSelected = false;
            });
          });
      });
    } else {
      if (this.isYouthMembership()) {
        // If youth membership, then whatever share selected by Primary Applicant applicable for all co applicant's
        this.coAppOptionalProducts.forEach((applicant) => {
          applicant.shares
            .filter((f) => f.id === account.id)
            .forEach((share) => {
              share.isSelected = true;
              share.isDisabled = true;
            });
        });
      }
    }
    this.emitEnableContinueButton({ page: 'optional-share' }); // trigger validation to enable continue button
    this.UpdateBundleIndex();
    this.updateSelection();
  }

  public emitEnableContinueButton(obj): void {
    this.enableContinueButton.next(obj);
  }
  // On Selection of Co Applicant's Share checkbox
  public coAppShareSelection(parentAccount: Product, coApplicantProduct: ApplicantProduct, coAppProducts: ApplicantProduct[]): void {
    const shareId = parentAccount.id;
    const isSelected = coApplicantProduct.shares.filter((f) => f.id === shareId)[0].isSelected;
    const rawReqBody = [];

    // Set Co Applicant Share Selection Status
    rawReqBody.push(this.generateRequestBody(coApplicantProduct.bundlePath, parentAccount.bundleIndex, 'isSelected', isSelected, null));

    // Iterate the co applicant's Optional Service & set its visibility. If co app share
    // selected then make respective applicant's services visible
    coApplicantProduct.shares
      // eslint-disable-next-line eqeqeq
      .filter((f) => f.id == shareId)
      .forEach((Share) => {
        Share.optionalServices.forEach((service) => {
          service.isVisible = isSelected;
          rawReqBody.push(
            this.generateRequestBody(coApplicantProduct.bundlePath, Share.bundleIndex, 'isVisible', service.isVisible, service.bundleIndex)
          );
          if (!isSelected) {
            service.isSelected = isSelected;
            rawReqBody.push(
              this.generateRequestBody(
                coApplicantProduct.bundlePath,
                Share.bundleIndex,
                'isSelected',
                service.isSelected,
                service.bundleIndex
              )
            );
          }
        });
      });

    // Check whether max allowed applicant selection met or not, if so, then disable other shares
    const maxAllowedApplicant = parentAccount.numberOfJointApplicants;
    let selectedCoApp = 0;

    // Count Total Selected co Applicant Share
    coAppProducts.forEach((applicant) => {
      applicant.shares
        .filter((f) => f.id === shareId)
        .forEach((item) => {
          selectedCoApp = item.isSelected ? selectedCoApp + 1 : selectedCoApp;
        });
    });

    // If Max Allowed Co App selection equals to selected co app shares then disable remaing co applicant
    if (selectedCoApp === maxAllowedApplicant) {
      coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .forEach((share) => {
            share.isDisabled = share.isSelected ? false : true;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled, null));
            if (share.isDisabled) {
              share.optionalServices.forEach((service) => {
                service.isVisible = false;
                rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isVisible', false, service.bundleIndex));
              });
            }
          });
      });
    } else {
      coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .forEach((share) => {
            share.isDisabled = false;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled, null));
            share.optionalServices.forEach((service) => {
              service.isVisible = share.isSelected;
              rawReqBody.push(
                this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isVisible', share.isSelected, service.bundleIndex)
              );
            });
          });
      });
    }

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.updateSelection();
  }

  // Save/Remove data into bundle when optional services selected/removed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public setOptioanalServiceSelectionForRequired(account: Product, option: any): void {
    const rawReqBody = [];

    // set Parent Applicant - Optional Serices
    rawReqBody.push(this.generateRequestBody('primaryApplicant', account.bundleIndex, 'isSelected', option.isSelected, option.bundleIndex));

    // Set Co Applicants - Selection
    // If Primary Applicant's Service Selection is False, then make co applicant's service selectiuon as well false
    this.coAppProducts.forEach((applicant) => {
      applicant.shares
        .filter((f) => f.id === account.id)
        .forEach((share) => {
          share.optionalServices
            .filter((o) => o.name === option.name)
            .forEach((service) => {
              if (!option.isSelected) {
                service.isSelected = false;
                rawReqBody.push(
                  this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isSelected', service.isSelected, service.bundleIndex)
                );
              } else if (
                option.isSelected &&
                (account.numberOfJointApplicants === 0 || this.isYouthMembership()) &&
                service.availableForJointOwner
              ) {
                service.isSelected = true;
                if (this.isYouthMembership()) {
                  service.isVisible = true;
                  rawReqBody.push(
                    this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isVisible', service.isVisible, service.bundleIndex)
                  );
                }
                rawReqBody.push(
                  this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isSelected', service.isSelected, service.bundleIndex)
                );
              }
            });
        });
    });

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.updateSelection();
  }

  // Save/Remove data into bundle when optional services selected/removed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public setOptionalServiceSelection(account: Product, option: any): void {
    const rawReqBody = [];

    // set Parent Applicant - Optional Serices
    rawReqBody.push(this.generateRequestBody('primaryApplicant', account.bundleIndex, 'isSelected', option.isSelected, option.bundleIndex));

    // Set Co Applicants - Selection
    // If Primary Applicant's Service Selection is False, then make co applicant's service selectiuon as well false
    this.coAppOptionalProducts.forEach((applicant) => {
      applicant.shares
        .filter((f) => f.id === account.id)
        .forEach((share) => {
          share.optionalServices
            .filter((o) => o.name === option.name)
            .forEach((service) => {
              if (!option.isSelected) {
                service.isSelected = false;
                rawReqBody.push(
                  this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isSelected', service.isSelected, service.bundleIndex)
                );
              } else if (
                option.isSelected &&
                (account.numberOfJointApplicants === 0 || this.isYouthMembership()) &&
                service.availableForJointOwner
              ) {
                service.isSelected = true;
                if (this.isYouthMembership()) {
                  service.isVisible = true;
                  rawReqBody.push(
                    this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isVisible', service.isVisible, service.bundleIndex)
                  );
                }
                rawReqBody.push(
                  this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isSelected', service.isSelected, service.bundleIndex)
                );
              }
            });
        });
    });

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.updateSelection();
  }

  /** Co Applicant's Service Selection */
  public coAppServiceSelection(bundlePath: string, shareBundleIndex: number, serviceBundleIndex: number, value: boolean): void {
    const rawReqBody = [];
    rawReqBody.push(this.generateRequestBody(bundlePath, shareBundleIndex, 'isSelected', value, serviceBundleIndex));
    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);

    this.updateSelection();
  }

  // On Selection of Co Applicant's Share checkbox
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public coAppMembershipServiceSelection(bundlePath: string, index: number, val: any): void {
    const rawReqBody = [
      {
        op: 'add',
        path: `oaoMemberInformation/accountsAndServices/${bundlePath}/membershipServices/${index}/isSelected`,
        value: val
      }
    ];
    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.updateSelection();
  }

  /** Generate Request Body */
  public generateRequestBody(
    shareBundlePath: string,
    shareBundleIndex: number,
    propName: string,
    value: boolean,
    serviceBundleIndex: number = null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any {
    if (serviceBundleIndex === null) {
      return {
        op: 'add',
        path: `oaoMemberInformation/accountsAndServices/${shareBundlePath}/shares/${shareBundleIndex}/${propName}`,
        value
      };
    } else {
      return {
        op: 'add',
        // eslint-disable-next-line max-len
        path: `oaoMemberInformation/accountsAndServices/${shareBundlePath}/shares/${shareBundleIndex}/OptionalServices/${serviceBundleIndex}/${propName}`,
        value
      };
    }
  }

  /** Can show Co Applicants for Optional Services */
  public showCoApplicantsForOptionalServices(shareId: number): boolean {
    let selectionCount = 0;
    this.coAppProducts.forEach((applicant) => {
      selectionCount += applicant.shares.filter((f) => f.id === shareId && f.isSelected === true).length;
    });
    return selectionCount > 0;
  }

  /** Can show Co Applicants for Optional Services */
  public showCoApplicantsOptionalForOptionalServices(shareId: number): boolean {
    let selectionCount = 0;
    this.coAppOptionalProducts.forEach((applicant) => {
      selectionCount += applicant.shares.filter((f) => f.id === shareId && f.isSelected === true).length;
    });
    return selectionCount > 0;
  }

  // Update isSelected property of Account/Optionsl Servies
  public UpdateBundleIndex(): void {
    this.l360Service.UpdateBundleIndex(this.allProducts).subscribe(() => {
      this.bundleHelperService.refreshBundle();
    });
  }

  /** Saves the user selection to session storage */
  public updateSelection(): void {
    this.statefulService.wrap('ss:oao-accounts-and-services').value = JSON.stringify(this.allProducts);
  }

  /** Check has coapplicants */
  public hasCoApplicant(): boolean {
    const membershipType = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').value;
    if (membershipType && membershipType.toString().toLowerCase() === 'business') {
      const responsibleParties = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
      return responsibleParties && responsibleParties > 1;
    }
    return this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
  }

  /** Check has optional Services */
  public hasCoApplicantShares(indexParent: number): boolean {
    // Check Is any co-applicant has the same share & its optional params
    const hasCoApplicantShares = [];
    this.coAppProducts.forEach((applicant) => {
      if (applicant.shares[indexParent]) {
        hasCoApplicantShares.push(true);
      }
    });
    // eslint-disable-next-line eqeqeq
    return hasCoApplicantShares.length > 0 && hasCoApplicantShares.filter((f) => f == true).length > 0;
  }

  /** Check has optional Services */
  public hasCoApplicantOptionalShares(indexParent: number): boolean {
    // Check Is any co-applicant has the same share & its optional params
    const hasCoApplicantShares = [];
    this.coAppOptionalProducts.forEach((applicant) => {
      if (applicant.shares[indexParent]) {
        hasCoApplicantShares.push(true);
      }
    });
    // eslint-disable-next-line eqeqeq
    return hasCoApplicantShares.length > 0 && hasCoApplicantShares.filter((f) => f).length > 0;
  }

  /** Check has optional Services */
  public hasOptionalServices(): boolean {
    const hasOptionalServices = [];
    this.coAppProducts.forEach((applicant) => {
      applicant.shares.forEach((share) => {
        hasOptionalServices.push(share.optionalServices.length > 0 ? true : false);
      });
    });
    return hasOptionalServices.length > 0 && hasOptionalServices.filter((f) => f === true).length > 0;
  }

  /** Check has optional Services */
  public hasOptionalProductsOptionalServices(): boolean {
    const hasOptionalServices = [];
    this.coAppOptionalProducts.forEach((applicant) => {
      applicant.shares.forEach((share) => {
        hasOptionalServices.push(share.optionalServices.length > 0 ? true : false);
      });
    });
    return hasOptionalServices.length > 0 && hasOptionalServices.filter((f) => f === true).length > 0;
  }

  public isYouthMembership(): boolean {
    const value = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').valueString;
    return value ? JSON.parse(value) : false;
  }

  /** check Joint Applicant availability. If youth membership, no need to check this condition.*/
  public hasJointApplicants(product: Product): boolean {
    if (this.isYouthMembership()) {
      return true;
    } else {
      return product.numberOfJointApplicants && product.numberOfJointApplicants > 0;
    }
  }

  /** Saves the user selection to session storage */
  public updateOptionalSelection(service: MembershipService): void {
    this.statefulService.wrap(this.arrayStorageId + '/$/isSelected', service.bundleIndex).value = service.isSelected;

    this.coApplicantProducts.forEach((applicant) => {
      applicant.membershipServices
        .filter((f) => f.relatedProductId === service.relatedProductId)
        .forEach((share) => {
          // if Primary Applicant's service unSelected, then clear all Co-Applicant's selection
          if (!service.isSelected) {
            share.isSelected = false;
            if (this.hasCoApplicant()) {
              this.setCoApplicantSelection(service.bundleIndex, false);
            }
          } else {
            // If youth membership, then whatever share selected by Primary Applicant applicable for all co applicant's
            if (this.isYouthMembership() && service.availableForJointOwner) {
              share.isSelected = true;
              this.setCoApplicantSelection(service.bundleIndex, true);
            }
          }
        });
    });
    this.updateServiceSelection();
  }

  public setCoApplicantSelection(bunldeIndex: number, val: boolean) {
    // bundle update for co-applicants
    if (this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/coApplicant/applicantId').value) {
      this.statefulService.wrap(this.coAppStorageId, bunldeIndex).value = val;
    }

    if (this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/additionalCoApplicant/applicantId').value) {
      this.statefulService.wrap(this.additionalCoAppStorageId, bunldeIndex).value = val;
    }

    if (this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/responsibleParties4/applicantId').value) {
      this.statefulService.wrap(this.responsibleParties4StorageId, bunldeIndex).value = val;
    }
  }

  /** Saves the user selection to session storage */
  private updateServiceSelection(): void {
    this.statefulService.wrap('ss:oao-accounts-membership-services').value = JSON.stringify(this.allServices);
  }
}
